import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Lock, FileText, Download, Loader2, PlayCircle } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import EmptyState from "@/components/EmptyState";
import MediaEmbed from "@/components/MediaEmbed";
import AudioPlayer from "@/components/AudioPlayer";
import Attachments from "@/components/Attachments";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { toast } from "sonner";

export default function CourseDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [c, setC] = useState(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => setC(r.data)).catch(() => setC(null));
  }, [id]);

  const buy = async () => {
    if (!user) { login(); return; }
    if (c.access === "premium") { navigate("/premium"); return; }
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: c.lookup_key, origin_url: window.location.origin,
        item_type: "course", item_id: c.id,
      });
      window.location.href = r.data.checkout_url;
    } catch { toast.error("Errore checkout"); setBusy(false); }
  };

  if (c === undefined) return <div className="max-w-4xl mx-auto px-4 py-24"><div className="h-80 bg-[#FFFFFF] animate-pulse rounded-2xl" /></div>;
  if (c === null) return <div className="pt-10"><EmptyState title={t("not_found")} desc={t("empty_desc")} /></div>;

  const renderLesson = (l) => {
    const open = c.unlocked || l.preview;
    if (!open) {
      return <div className="flex items-center gap-2 text-[#9C8F94] text-sm"><Lock className="w-4 h-4 text-[#C6899E]" /> {t("locked")}</div>;
    }
    return (
      <div className="space-y-4">
        {(l.media_kind && l.media_kind !== "none") && (
          l.type === "audio" && l.media_kind === "upload" && l.media_path
            ? <AudioPlayer path={l.media_path} title={l.title} />
            : <MediaEmbed kind={l.media_kind} url={l.media_url} path={l.media_path} />
        )}
        {l.text && <p className="prose-unfiltered !text-base">{l.text}</p>}
        {l.downloads?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {l.downloads.map((d, i) => (
              <a key={i} href={mediaUrl(d)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-xs text-[#C6899E] border border-[rgba(198,137,158,0.3)] rounded-full px-3 py-1.5 hover:bg-[rgba(198,137,158,0.1)]">
                <Download className="w-3.5 h-3.5" /> Download {i + 1}
              </a>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="course-detail" className="max-w-4xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-24">
      <button onClick={() => navigate("/corsi")} className="inline-flex items-center gap-2 text-sm text-[#9C8F94] hover:text-[#C6899E] mb-10" data-testid="course-back">
        <ArrowLeft className="w-4 h-4" /> {t("courses_title")}
      </button>

      <div className="rounded-2xl overflow-hidden mb-10 aspect-[16/7] bg-[#F3E8E2] border border-[rgba(61,50,56,0.08)]">
        {c.cover_path ? <img src={mediaUrl(c.cover_path)} alt={c.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center font-serif text-6xl text-[rgba(198,137,158,0.2)]">{c.title.charAt(0)}</div>}
      </div>

      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-tight">{c.title}</h1>
          {c.description && <p className="mt-4 prose-unfiltered">{c.description}</p>}
        </div>
        {!c.unlocked && (
          <button onClick={buy} disabled={busy} data-testid="course-cta"
            className="inline-flex items-center gap-2 bg-[#C6899E] text-[#4A2233] rounded-full px-7 py-3.5 text-sm uppercase tracking-[0.15em] hover:bg-[#B4718C] transition-colors disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            {c.access === "premium" ? t("unlock") : c.price > 0 ? `${t("buy")} · €${c.price}` : t("enter")}
          </button>
        )}
      </div>

      {c.attachments?.length > 0 && (
        <div className="mt-8">
          <Attachments files={c.attachments} label="Materiali del corso" />
        </div>
      )}

      <div className="mt-12">
        <Accordion type="single" collapsible className="space-y-4" defaultValue="m-0">
          {(c.modules || []).map((m, mi) => (
            <AccordionItem key={m.id} value={`m-${mi}`} className="rounded-2xl border border-[rgba(61,50,56,0.08)] bg-[#FFFFFF] px-6 overflow-hidden" data-testid={`module-${mi}`}>
              <AccordionTrigger className="hover:no-underline py-5">
                <span className="font-serif text-xl text-[#3D3238] text-left">
                  <span className="text-[#C6899E] mr-3">{String(mi + 1).padStart(2, "0")}</span>{m.title}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="space-y-6">
                  {(m.lessons || []).map((l) => (
                    <div key={l.id} className="border-l border-[rgba(198,137,158,0.2)] pl-5" data-testid={`lesson-${l.id}`}>
                      <div className="flex items-center gap-2 mb-3">
                        {l.type === "text" ? <FileText className="w-4 h-4 text-[#C6899E]" /> : <PlayCircle className="w-4 h-4 text-[#C6899E]" />}
                        <span className="text-[#3D3238]">{l.title}</span>
                        {l.preview && !c.unlocked && <span className="text-[0.6rem] uppercase tracking-[0.12em] text-[#C6899E] border border-[rgba(198,137,158,0.3)] rounded-full px-2 py-0.5">Preview</span>}
                      </div>
                      {renderLesson(l)}
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
